/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { useWindowDimensions } from 'react-native';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore'
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities'
import { useBranchBarcodeInputModeForCurrentBranch } from '../../shell/session/hooks/useBranchBarcodeInputMode';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { usePosStore } from '../store/usePosStore';
import { usePosCashSessionStore } from '../store/usePosCashSessionStore';
import { usePosOverlayStore } from '../store/usePosOverlayStore';
import { usePosCheckoutStore } from '../store/usePosCheckoutStore';
import { useShallow } from 'zustand/react/shallow';
import { isPlaywrightE2E, isPlaywrightE2EBridge } from '../../shared/playwrightE2e';
import type { ProductSearchRow } from '../../catalog/domain-api';
import { usePosCatalogPane } from './hooks/usePosCatalogPane';
import { usePosCartPane } from './hooks/usePosCartPane';
import { usePosCheckoutPane } from './hooks/usePosCheckoutPane';
import { usePosOverlaysPane } from './hooks/usePosOverlaysPane';
import { usePosPermissionsPane } from './hooks/usePosPermissionsPane';
import { usePosPricedAddToCart } from './hooks/usePosPricedAddToCart';
import { usePosPriceListStore } from '../store/usePosPriceListStore';
import { usePosOperationalPane } from './hooks/usePosOperationalPane';
import { usePosOperationalAlerts } from './hooks/usePosOperationalAlerts';
import { resolvePosStockSalePolicy } from '../application/posInventoryStockUx';
import type { PosStockSalePolicy } from '../domain/posTypes';
import type { Product } from '../../catalog/domain-api';
import { getProductById } from '../../catalog/infra-api';
import { showInfo, showError } from '../../shared/ui/appToast';

type CatalogPaneReturn = ReturnType<typeof usePosCatalogPane>;
type CartPaneReturn = ReturnType<typeof usePosCartPane>;
type CheckoutPaneReturn = ReturnType<typeof usePosCheckoutPane>;
type OverlaysPaneReturn = ReturnType<typeof usePosOverlaysPane>;
type PermissionsPaneReturn = ReturnType<typeof usePosPermissionsPane>;
type PosStoreState = ReturnType<typeof usePosStore.getState>;

/** Params when navigating Agenda → POS («Cobrar en POS»), OT → POS («Cobrar») o Mesas → POS para ronda. */
export type PosScreenParams = {
  appointmentId?: string;
  workOrderId?: string;
  diningTableId?: string;
  /** Cambia en cada apertura desde Mesas para forzar re-hidratación del carrito. */
  tableOpenKey?: string;
  /** Cobrar desde ficha: no volcar Pendiente a Cuenta hasta vender. */
  tablesideCheckoutPreview?: boolean;
};

type PosRoute = RouteProp<{ POS: PosScreenParams }, 'POS'>;

export type PosScreenSharedProps = {
  canEdit: boolean;
  canOpenCashManagementFromPos: boolean;
  mercadopagoQrMethodAllowed: boolean;
  allowFiscalSaleMode: boolean;
  userId: string | null | undefined;
};

export type PosScreenCatalogProps = CatalogPaneReturn & {
  refreshing: boolean;
  handleRefresh: () => Promise<void>;
  handleProductPress: OverlaysPaneReturn['handleProductPress'];
  handlePackagePress: OverlaysPaneReturn['handlePackagePress'];
  addedFlashProductId: OverlaysPaneReturn['addedFlashProductId'];
  importCategoryOptions: ReturnType<typeof usePosOperationalPane>['importCategoryOptions'];
  defaultImportCategoryId: OverlaysPaneReturn['defaultImportCategoryId'];
  selectedCategory: string | 'all' | null;
  stockSalePolicy: PosStockSalePolicy;
};

export type PosScreenCartProps = CartPaneReturn & {
  mobileCartSheetVisible: boolean;
  setMobileCartSheetVisible: (visible: boolean) => void;
  addProductToCart: PosStoreState['addProductToCart'];
  isApplying: boolean;
  isClearing: boolean;
};

export type PosScreenCheckoutProps = CheckoutPaneReturn & {
  currentBranchId: string | null | undefined;
};

export type PosScreenOverlaysProps = OverlaysPaneReturn & {
  setScannerVisible: (visible: boolean) => void;
  barcodeAllowCamera: boolean;
};

export type PosScreenModuleReturn = {
  shared: PosScreenSharedProps;
  catalog: PosScreenCatalogProps;
  cart: PosScreenCartProps;
  checkout: PosScreenCheckoutProps;
  overlays: PosScreenOverlaysProps;
  operationalAlerts: ReturnType<typeof usePosOperationalAlerts>;
};

export const usePosScreenModule = (): PosScreenModuleReturn => {
  const navigation = useNavigation<any>();
  const route = useRoute<PosRoute>();
  const { width: windowWidth } = useWindowDimensions();
  const [mobileCartSheetVisible, setMobileCartSheetVisible] = useState(false);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const sessionUserId = useAuthStore((state) => state.session?.user?.id ?? null);
  const user = useAuthStore((state) => state.user);
  const branches = useSessionStore((state) => state.branches);
  const initialDiningTableId = route.params?.diningTableId?.trim() ?? null;
  const tableOpenKey = route.params?.tableOpenKey?.trim() ?? null;
  const tablesideCheckoutPreview = Boolean(route.params?.tablesideCheckoutPreview);
  const tableHandoffUiKeyRef = useRef<string | null>(null);

  const prevBranchIdRef = useRef(currentBranchId);
  useEffect(() => {
    if (prevBranchIdRef.current !== currentBranchId) {
      prevBranchIdRef.current = currentBranchId;
      usePosStore.getState().clearCart();
      usePosCheckoutStore.getState().resetCheckout();
      usePosOverlayStore.getState().resetAll();
    }
  }, [currentBranchId]);

  useEffect(() => {
    if (!initialDiningTableId || !tableOpenKey) return;
    const uiKey = `${initialDiningTableId}:${tableOpenKey}`;
    if (tableHandoffUiKeyRef.current === uiKey) return;
    const cartLen = usePosStore.getState().cart.length;
    if (cartLen === 0 && !tablesideCheckoutPreview) return;
    tableHandoffUiKeyRef.current = uiKey;
    setMobileCartSheetVisible(true);
  }, [initialDiningTableId, tableOpenKey, tablesideCheckoutPreview]);

  const permissions = usePosPermissionsPane({
    navigation,
    currentBranchId,
    sessionUserId,
  });

  const hasPriceLists = useHasBusinessCapability('price_lists');
  const hasCommissions = useHasBusinessCapability('commissions');
  const refreshPriceListCache = usePosPriceListStore((s) => s.refreshCache);

  const { selectedCategory, setCategory, addProductToCart: rawAddProductToCart } = usePosStore(
    useShallow((s) => ({
      selectedCategory: s.selectedCategory,
      setCategory: s.setCategory,
      addProductToCart: s.addProductToCart,
    }))
  );
  const addProductToCart = usePosPricedAddToCart(
    useCallback(
      (product: Product, quantityOrOpts: number | Parameters<typeof rawAddProductToCart>[1] = 1) => {
        if (!hasCommissions || typeof quantityOrOpts === 'number') {
          rawAddProductToCart(product, quantityOrOpts);
          return;
        }
        rawAddProductToCart(product, {
          ...quantityOrOpts,
          soldByUserId: quantityOrOpts.soldByUserId !== undefined
            ? quantityOrOpts.soldByUserId
            : user?.id ?? null,
        });
      },
      [hasCommissions, rawAddProductToCart, user?.id],
    ),
  );

  useEffect(() => {
    if (!hasPriceLists) return;
    void refreshPriceListCache();
  }, [hasPriceLists, refreshPriceListCache, currentBranchId]);

  const cartPane = usePosCartPane({
    currentBranchId,
    organizationId: permissions.organizationId,
    sessionUserId,
    canEdit: permissions.canEdit,
    canDiscount: permissions.canDiscount,
  });

  const catalogActionsRef = useRef({
    reloadCatalog: async () => {},
    reloadFilterOptions: () => {},
  });

  const operational = usePosOperationalPane({
    currentBranchId,
    routeName: route.name,
    catalogActionsRef,
    reloadPromotionsFromLocal: cartPane.reloadPromotionsFromLocal,
    refreshPriceLists: hasPriceLists ? refreshPriceListCache : undefined,
  });

  const cashSession = usePosCashSessionStore(
    useShallow((s) => ({
      activeCashSessionId: s.activeCashSessionId,
      cashSessionLoading: s.cashSessionLoading,
      cashSessionHydratedForBranchId: s.cashSessionHydratedForBranchId,
      cashSessionOpenedAt: s.cashSessionOpenedAt,
      cashSessionOpeningAmount: s.cashSessionOpeningAmount,
    }))
  );
  const setScannerVisible = usePosOverlayStore((s) => s.setScannerVisible);
  const setManualBarcodeVisible = usePosOverlayStore((s) => s.setManualBarcodeVisible);
  const setVariosVisible = usePosOverlayStore((s) => s.setVariosVisible);
  const setRecargaVisible = usePosOverlayStore((s) => s.setRecargaVisible);
  const allowsExternalServices = useHasBusinessCapability('external_services');
  const allowsMiscQuickSale = useHasBusinessCapability('misc_quick_sale');
  const allowsBarcodeScan = useHasBusinessCapability('barcode_scan');
  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const cashSessionReady =
    Boolean(currentBranchId) && cashSession.cashSessionHydratedForBranchId === currentBranchId;
  const branchStockSalePolicy = !currentBranchId
    ? 'block'
    : branches.find((b) => b.id === currentBranchId)?.stockSalePolicy ?? 'block';
  const stockSalePolicy = resolvePosStockSalePolicy(branchStockSalePolicy, inventoryStockUxEnabled);
  const [lookupFeedback, setLookupFeedback] = useState<string | null>(null);
  const openCashMgmtFromCheckoutRef = useRef<(() => void) | undefined>(undefined);
  const posBarcodeHardwareSubmitRef = useRef<() => void>(() => {});

  const { allowCamera: barcodeAllowCameraBranch, allowHardwareWedge: barcodeAllowHardware } =
    useBranchBarcodeInputModeForCurrentBranch();
  const barcodeAllowCamera = barcodeAllowCameraBranch && allowsBarcodeScan;
  const playwrightE2eActive = isPlaywrightE2E || isPlaywrightE2EBridge();
  const allowPosBarcodeEnterSubmit = barcodeAllowHardware || playwrightE2eActive;

  const operationalAlerts = usePosOperationalAlerts({
    branchId: currentBranchId,
    mercadopagoQrMethodAllowed: permissions.mercadopagoQrMethodAllowed,
    openSalesHistory: permissions.openSalesHistory,
  });

  const handleProductPressRef = useRef<(product: ProductSearchRow) => void>(() => {});

  const catalog = usePosCatalogPane({
    currentBranchId,
    windowWidth,
    selectedCategory,
    setCategory,
    categories: operational.categories,
    canEdit: permissions.canEdit,
    canViewPos: permissions.canViewPos,
    canOpenCashManagementFromPos: permissions.canOpenCashManagementFromPos,
    trialBanner: permissions.trialBanner,
    activeCashSessionId: cashSession.activeCashSessionId,
    cashSessionLoading: cashSession.cashSessionLoading,
    cashSessionReady,
    cashSessionOpenedAt: cashSession.cashSessionOpenedAt,
    cashSessionOpeningAmount: cashSession.cashSessionOpeningAmount,
    handleOpenCashManagementFromCheckout: () => openCashMgmtFromCheckoutRef.current?.(),
    openSalesHistory: permissions.openSalesHistory,
    navigation,
    barcodeAllowCamera,
    allowPosBarcodeEnterSubmit,
    handlePosBarcodeHardwareSubmit: () => posBarcodeHardwareSubmitRef.current(),
    setScannerVisible,
    setManualBarcodeVisible,
    setVariosVisible,
    setRecargaVisible,
    allowsExternalServices,
    allowsMiscQuickSale,
    lookupFeedback,
    setLookupFeedback,
    hasKitchenCapability: permissions.hasKitchenCapability,
    diningTableId: initialDiningTableId,
    onProductPress: (product) => handleProductPressRef.current(product),
  });

  useEffect(() => {
    catalogActionsRef.current.reloadCatalog = catalog.reloadCatalog;
    catalogActionsRef.current.reloadFilterOptions = catalog.reloadFilterOptions;
  }, [catalog.reloadCatalog, catalog.reloadFilterOptions]);

  // Prefill cart from Agenda → Cobrar en POS
  const appointmentPrefillDone = useRef<string | null>(null);
  useEffect(() => {
    const appointmentId = route.params?.appointmentId?.trim();
    if (!appointmentId) {
      // Allow charging the same turno again after the param was cleared.
      appointmentPrefillDone.current = null;
      return;
    }
    if (!permissions.canEdit) return;
    if (appointmentPrefillDone.current === appointmentId) return;

    let cancelled = false;
    void (async () => {
      try {
        const { getAppointmentById } = await import('../../appointments/infra/appointmentsRepo');
        const { setPendingAppointmentCheckout } = await import(
          '../../appointments/application/appointmentPosBridge'
        );
        const appt = await getAppointmentById(appointmentId);
        if (cancelled || !appt) {
          if (!cancelled) showError('Turno', 'No encontramos el turno para cobrar.');
          return;
        }
        if (appt.saleId) {
          showInfo('Turno', 'Este turno ya tiene una venta.');
          navigation.setParams?.({ appointmentId: undefined } as PosScreenParams);
          return;
        }
        if (!appt.productId && !(appt.items && appt.items.length > 0)) {
          showError('Turno', 'Este turno no tiene un servicio.');
          return;
        }
        const productIds =
          appt.items && appt.items.length > 0
            ? appt.items.map((i) => i.productId)
            : [appt.productId];
        const products = [];
        for (const pid of productIds) {
          const product = await getProductById(pid);
          if (!product) {
            showError('Turno', 'No encontramos un servicio del turno en el catálogo.');
            return;
          }
          products.push(product);
        }
        if (cancelled) return;
        usePosStore.getState().clearCart();
        for (const product of products) {
          usePosStore.getState().addProductToCart(product, 1);
        }
        setPendingAppointmentCheckout(appointmentId, productIds[0], productIds);
        appointmentPrefillDone.current = appointmentId;
        setMobileCartSheetVisible(true);
        const label =
          products.length > 1
            ? `${products.length} servicios · ${appt.customerName}`
            : `${products[0]?.name ?? 'Servicio'} · ${appt.customerName}`;
        showInfo('Turno cargado', label);
        // Drop param so a later focus of POS does not re-prefill the same id.
        navigation.setParams?.({ appointmentId: undefined } as PosScreenParams);
      } catch (e) {
        presentUnexpectedFailure({ module: 'pos', op: 'turno', error: e, title: 'Turno', userMessage: 'No se pudo cargar el turno.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route.params?.appointmentId, permissions.canEdit, navigation]);

  // Prefill cart from OT → Cobrar en POS
  const workOrderPrefillDone = useRef<string | null>(null);
  useEffect(() => {
    const workOrderId = route.params?.workOrderId?.trim();
    if (!workOrderId) {
      workOrderPrefillDone.current = null;
      return;
    }
    if (!permissions.canEdit) return;
    if (workOrderPrefillDone.current === workOrderId) return;

    let cancelled = false;
    void (async () => {
      try {
        const { getWorkOrderDetail } = await import('../../work-orders/infra/workOrdersRepo');
        const { setPendingWorkOrderCheckout } = await import(
          '../../work-orders/application/workOrderPosBridge'
        );
        const { getSessionOrganizationId, getMiscProductIdForOrganization } = await import(
          '../../shell/session/sessionOrganization'
        );
        const detail = await getWorkOrderDetail(workOrderId);
        if (cancelled || !detail) {
          if (!cancelled) showError('OT', 'No encontramos la orden de trabajo para cobrar.');
          return;
        }
        const { workOrder, tasks, parts } = detail;
        const { peekPendingWorkOrderCheckout } = await import(
          '../../work-orders/application/workOrderPosBridge'
        );
        const pending = peekPendingWorkOrderCheckout();
        const kind = pending?.workOrderId === workOrderId ? pending.kind : 'full';
        const amountDue = pending?.workOrderId === workOrderId ? pending.amountDue : null;
        const otLabel =
          pending?.workOrderId === workOrderId
            ? pending.otLabel
            : `OT-${workOrder.otNumber ?? workOrder.id.slice(0, 8).toUpperCase()}`;

        if (workOrder.paymentStatus === 'paid' || workOrder.saleId) {
          showInfo('OT', 'Esta OT ya está cobrada.');
          navigation.setParams?.({ workOrderId: undefined } as PosScreenParams);
          return;
        }
        if (kind === 'full' && tasks.length === 0 && parts.length === 0) {
          showError('OT', 'La OT no tiene tareas ni repuestos para cobrar.');
          return;
        }
        if ((kind === 'deposit' || kind === 'balance') && (!amountDue || amountDue <= 0)) {
          showError('OT', 'Falta el monto a cobrar.');
          return;
        }
        if (cancelled) return;

        const orgId = await getSessionOrganizationId();
        const miscProductId = orgId ? await getMiscProductIdForOrganization(orgId) : null;

        usePosStore.getState().clearCart();

        const taskProductIds: string[] = [];
        const partProductIds: string[] = [];

        if (kind === 'deposit' || kind === 'balance') {
          if (!miscProductId) {
            showError('OT', 'No hay producto Varios para registrar la seña/saldo.');
            return;
          }
          const label =
            kind === 'deposit' ? `Seña ${otLabel}` : `Saldo ${otLabel}`;
          usePosStore.getState().addCustomItemToCart({
            name: label,
            amount: amountDue!,
            miscProductId,
          });
        } else {
          for (const task of tasks) {
            taskProductIds.push(task.productId);
            const product = await getProductById(task.productId);
            if (product) {
              usePosStore.getState().addProductToCart(product, {
                quantity: task.quantity,
                unitPrice: task.unitPrice,
              });
            } else if (miscProductId) {
              usePosStore.getState().addCustomItemToCart({
                name: task.productName || `Tarea ${task.productId.slice(0, 6)}`,
                amount: task.unitPrice * task.quantity,
                miscProductId,
              });
            }
          }

          for (const part of parts) {
            partProductIds.push(part.productId);
            const product = await getProductById(part.productId);
            if (product) {
              usePosStore.getState().addProductToCart(product, {
                quantity: part.quantity,
                unitPrice: part.unitPrice,
              });
            } else if (miscProductId) {
              usePosStore.getState().addCustomItemToCart({
                name: part.productName || `Repuesto ${part.productId.slice(0, 6)}`,
                amount: part.unitPrice * part.quantity,
                miscProductId,
              });
            }
          }
        }

        setPendingWorkOrderCheckout(workOrderId, taskProductIds, partProductIds, {
          kind,
          amountDue,
          otLabel,
        });
        workOrderPrefillDone.current = workOrderId;
        setMobileCartSheetVisible(true);
        showInfo(
          'OT cargada',
          `${otLabel} · ${workOrder.customerName || 'Sin cliente'}`,
        );
        navigation.setParams?.({ workOrderId: undefined } as PosScreenParams);
      } catch (e) {
        presentUnexpectedFailure({ module: 'pos', op: 'ot', error: e, title: 'OT', userMessage: 'No se pudo cargar la OT.' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [route.params?.workOrderId, permissions.canEdit, navigation]);

  const overlays = usePosOverlaysPane({
    navigation,
    canEdit: permissions.canEdit,
    stockSalePolicy,
    businessProfile: permissions.businessProfile,
    currentBranchId,
    canImportFromCatalog: permissions.canImportFromCatalog,
    entitlements: permissions.entitlements,
    userId: user?.id,
    categories: operational.categories,
    setCategories: operational.setCategories,
    setCategory,
    searchQuery: catalog.searchQuery,
    setSearchQuery: catalog.setSearchQuery,
    setLookupFeedback,
    reloadCatalog: catalog.reloadCatalog,
    allowPosBarcodeEnterSubmit,
    posBarcodeHardwareSubmitRef,
  });

  handleProductPressRef.current = overlays.handleProductPress;

  const checkout = usePosCheckoutPane({
    navigation,
    currentBranchId,
    cashSessionReady,
    initialDiningTableId,
    tableOpenKey,
    tablesideCheckoutPreview,
    stockSalePolicy,
    cart: cartPane.cart,
    cartEvaluation: cartPane.cartEvaluation,
    promoUiStatus: cartPane.promoUiStatus,
    saleItems: cartPane.saleItems,
    discretionaryDiscountTotal: cartPane.discretionaryDiscountTotal,
    appliedPromoCodes: cartPane.appliedPromoCodes,
    selectedCashierPromotionId: cartPane.selectedCashierPromotionId,
    resolveEvaluationForCheckout: cartPane.resolveEvaluationForCheckout,
    clearCart: cartPane.clearCart,
    userId: user?.id,
    canOpenCashManagementFromPos: permissions.canOpenCashManagementFromPos,
    mercadopagoQrMethodAllowed: permissions.mercadopagoQrMethodAllowed,
    customerMethodAllowed: permissions.customerMethodAllowed,
    checkMethodAllowed: permissions.checkMethodAllowed,
    allowFiscalSaleMode: permissions.allowFiscalSaleMode,
    defaultCheckoutFiscalMode: permissions.defaultCheckoutFiscalMode,
    hospitalityEnabled: permissions.hospitalityEnabled,
    hasKitchenCapability: permissions.hasKitchenCapability,
    hasTablesCapability: permissions.hasTablesCapability,
    activeDraftId: cartPane.activeDraftId,
    clearPersistenceAfterSale: cartPane.clearPersistenceAfterSale,
    reloadCatalog: catalog.reloadCatalog,
    setMobileCartSheetVisible,
  });

  useEffect(() => {
    openCashMgmtFromCheckoutRef.current = checkout.handleOpenCashManagementFromCheckout;
  }, [checkout.handleOpenCashManagementFromCheckout]);

  return {
    shared: {
      canEdit: permissions.canEdit,
      canOpenCashManagementFromPos: permissions.canOpenCashManagementFromPos,
      mercadopagoQrMethodAllowed: permissions.mercadopagoQrMethodAllowed,
      allowFiscalSaleMode: permissions.allowFiscalSaleMode,
      userId: user?.id,
    },
    catalog: {
      ...catalog,
      refreshing: operational.refreshing,
      handleRefresh: operational.handleRefresh,
      handleProductPress: overlays.handleProductPress,
      handlePackagePress: overlays.handlePackagePress,
      addedFlashProductId: overlays.addedFlashProductId,
      importCategoryOptions: operational.importCategoryOptions,
      defaultImportCategoryId: overlays.defaultImportCategoryId,
      selectedCategory,
      stockSalePolicy,
    },
    cart: {
      ...cartPane,
      mobileCartSheetVisible,
      setMobileCartSheetVisible,
      addProductToCart,
    },
    checkout: {
      ...checkout,
      currentBranchId,
    },
    overlays: {
      ...overlays,
      setScannerVisible,
      barcodeAllowCamera,
    },
    operationalAlerts,
  };
};
